import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiPost } from "../lib/api";
import { CATEGORIES, CONDITIONS } from "../lib/categories";
import { PhotoUploader } from "../components/PhotoUploader";
import { SizeField } from "../components/SizeField";
import { Button, Field, Input, Textarea, Select, Alert, Spinner } from "../components/ui";

export function CreateListingPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    category: "",
    condition: "good",
    size: "",
    price: "",
    description: "",
  });
  const [photos, setPhotos] = useState([]); // data URLs
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(""); // progress hint

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.title.trim()) return setError("Please add a title.");
    if (!form.category) return setError("Please choose a category.");
    const price = Number(form.price);
    if (!Number.isFinite(price) || price < 0) return setError("Enter a valid price (0 or more).");

    setBusy(true);
    try {
      let photoUrls = [];
      let thumbs = [];
      if (photos.length) {
        setStep("Uploading photos…");
        const { urls, thumbUrls } = await apiPost("/photo-upload", { images: photos });
        photoUrls = urls;
        thumbs = thumbUrls || [];
      }
      setStep("Publishing listing…");
      const { listing } = await apiPost("/listings", {
        title: form.title.trim(),
        category: form.category,
        condition: form.condition,
        size: form.size.trim(),
        price,
        description: form.description.trim(),
        photos: photoUrls,
        thumbs,
      });
      navigate(`/gear/listing/${listing.id}`);
    } catch (err) {
      setError(err.message);
      setBusy(false);
      setStep("");
    }
  }

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-800 mb-1">List an item</h1>
      <p className="text-sm text-slate-500 mb-5">Add photos, set your price, and reach fellow SSA members.</p>

      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Photos">
          <PhotoUploader value={photos} onChange={setPhotos} />
        </Field>

        <Field label="Title">
          <Input value={form.title} onChange={set("title")} placeholder="Nike Mercurial cleats — youth 3Y" maxLength={100} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <Select value={form.category} onChange={set("category")}>
              <option value="">Choose…</option>
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Condition">
            <Select value={form.condition} onChange={set("condition")}>
              {CONDITIONS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Price (USD)">
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.price}
              onChange={set("price")}
              placeholder="25"
            />
          </Field>
          <SizeField
            category={form.category}
            value={form.size}
            onChange={(size) => setForm((f) => ({ ...f, size }))}
          />
        </div>

        <Field label="Description">
          <Textarea rows={4} value={form.description} onChange={set("description")} maxLength={2000} />
        </Field>

        <Alert>{error}</Alert>

        <div className="flex gap-2">
          <Button type="button" variant="secondary" onClick={() => navigate(-1)} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy} className="flex-1">
            {busy ? (
              <>
                <Spinner /> {step || "Working…"}
              </>
            ) : (
              "Publish listing"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
